<script setup>
import { computed, ref } from 'vue'
import useEmailSettings from '@modules/setting/composables/whitelabel/useEmailSettings'
import CstButton from '@ui/Button/CstButton.vue'
import useWhiteLabel from '@modules/setting/composables/whitelabel/useWhiteLabel'

const emit = defineEmits(['prev'])

const {
  smtpHost,
  smtpPort,
  smtpUsername,
  smtpPassword,
  fromEmail,
  senderName,
  smtpEncryption,
  isLoading,
  shouldDisableNext,
  isFormValid,
  testSMTPConnection,
  handleSubmit,
} = useEmailSettings()

const {
  isSetupComplete,
  isWhiteLabelEnabled,
  isEditing,
  currentStep,
  completedSteps,
  toggleWhiteLabelActiveStatus,
} = useWhiteLabel()

const refreshCompletedSteps = () => {
  if (isSetupComplete.value && isWhiteLabelEnabled.value) {
    completedSteps.value = [true, true, true];
  }
};

const onSubmit = async () => {
  if (await handleSubmit()) {
    if (!isWhiteLabelEnabled.value) {
      await toggleWhiteLabelActiveStatus();
      isSetupComplete.value = true;
    }
    currentStep.value = 0;
    isEditing.value = false;
    refreshCompletedSteps();
  }
};

const showPassword = ref(false)

const PasswordEyeIconSrc = computed(() => {
  return showPassword.value ? '/img/eye_disabled.svg' : '/img/eye_allowed.svg'
})

const togglePassword = () => {
  showPassword.value = !showPassword.value
}

const validateDigits = () => {
  smtpPort.value.value = smtpPort.value.value?.replace(/\D/g, '')
}
</script>

<template>
  <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-lg">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg
          fill="none"
          height="23"
          viewBox="0 0 23 23"
          width="23"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.4989 16.0527C11.7027 16.0527 11.8734 15.9838 12.0109 15.846C12.1486 15.7083 12.2174 15.5376 12.2174 15.334V11.2611C12.2174 11.0574 12.1485 10.8867 12.0107 10.7488C11.8728 10.6112 11.7021 10.5423 11.4985 10.5423C11.2947 10.5423 11.124 10.6112 10.9865 10.7488C10.8488 10.8867 10.7799 11.0574 10.7799 11.2611V15.334C10.7799 15.5376 10.8489 15.7083 10.9867 15.846C11.1245 15.9838 11.2953 16.0527 11.4989 16.0527ZM11.4987 8.90213C11.718 8.90213 11.9018 8.82794 12.0502 8.67956C12.1986 8.53118 12.2728 8.34733 12.2728 8.12804C12.2728 7.90874 12.1986 7.7249 12.0502 7.57652C11.9018 7.42829 11.718 7.35418 11.4987 7.35418C11.2794 7.35418 11.0956 7.42829 10.9472 7.57652C10.7988 7.7249 10.7246 7.90874 10.7246 8.12804C10.7246 8.34733 10.7988 8.53118 10.9472 8.67956C11.0956 8.82794 11.2794 8.90213 11.4987 8.90213ZM11.5004 20.6048C10.2411 20.6048 9.0575 20.3659 7.94951 19.888C6.84152 19.4101 5.87775 18.7615 5.05822 17.9423C4.23868 17.1231 3.58981 16.1597 3.1116 15.0522C2.63356 13.9447 2.39453 12.7614 2.39453 11.5023C2.39453 10.2431 2.63348 9.05946 3.11136 7.95146C3.58925 6.84347 4.23781 5.87971 5.05702 5.06017C5.87624 4.24064 6.8396 3.59177 7.94711 3.11356C9.05463 2.63551 10.2379 2.39648 11.497 2.39648C12.7563 2.39648 13.9399 2.63543 15.0479 3.11332C16.1559 3.59121 17.1196 4.23976 17.9392 5.05897C18.7587 5.87819 19.4076 6.84155 19.8858 7.94907C20.3638 9.05658 20.6029 10.2399 20.6029 11.499C20.6029 12.7582 20.3639 13.9418 19.886 15.0498C19.4081 16.1578 18.7596 17.1216 17.9404 17.9411C17.1212 18.7607 16.1578 19.4095 15.0503 19.8877C13.9428 20.3658 12.7595 20.6048 11.5004 20.6048ZM11.4987 19.1673C13.639 19.1673 15.4518 18.4246 16.9372 16.9392C18.4227 15.4538 19.1654 13.6409 19.1654 11.5007C19.1654 9.36037 18.4227 7.54753 16.9372 6.06211C15.4518 4.57669 13.639 3.83398 11.4987 3.83398C9.35842 3.83398 7.54557 4.57669 6.06016 6.06211C4.57474 7.54753 3.83203 9.36037 3.83203 11.5007C3.83203 13.6409 4.57474 15.4538 6.06016 16.9392C7.54557 18.4246 9.35842 19.1673 11.4987 19.1673Z"
            fill="#0068E5"
          />
        </svg>
      </div>
      <div class="ml-3">
        <p class="text-sm text-[#0068E5]">
          Utilize your own SMTP server to send emails from your domain. All
          emails from your whitelabel domain will be sent using this SMTP
          server.
        </p>
      </div>
    </div>
  </div>

  <div class="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6 rounded-lg">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg
          class="h-5 w-5 text-[#E67700]"
          fill="none"
          height="19"
          viewBox="0 0 18 19"
          width="18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.50125 3.55859C4.15425 3.55859 3.859 3.68001 3.6155 3.92284C3.37183 4.16568 3.25 4.46051 3.25 4.80734C3.25 5.15434 3.37142 5.44959 3.61425 5.69309C3.85708 5.93676 4.15192 6.05859 4.49875 6.05859C4.84575 6.05859 5.141 5.93718 5.3845 5.69434C5.62817 5.45151 5.75 5.15659 5.75 4.80959C5.75 4.46276 5.62858 4.16751 5.38575 3.92384C5.14292 3.68034 4.84808 3.55859 4.50125 3.55859ZM4.50125 12.9431C4.15425 12.9431 3.859 13.0645 3.6155 13.3073C3.37183 13.5502 3.25 13.8451 3.25 14.1921C3.25 14.5389 3.37142 14.8342 3.61425 15.0778C3.85708 15.3213 4.15192 15.4431 4.49875 15.4431C4.84575 15.4431 5.141 15.3217 5.3845 15.0788C5.62817 14.836 5.75 14.5412 5.75 14.1943C5.75 13.8473 5.62858 13.5521 5.38575 13.3086C5.14292 13.0649 4.84808 12.9431 4.50125 12.9431ZM1.38475 0.808594H16.6152C16.8659 0.808594 17.0761 0.893178 17.2458 1.06234C17.4153 1.23134 17.5 1.44084 17.5 1.69084V7.86609C17.5 8.13676 17.4153 8.36151 17.2458 8.54034C17.0761 8.71918 16.8659 8.80859 16.6152 8.80859H1.38475C1.13408 8.80859 0.923917 8.71918 0.75425 8.54034C0.58475 8.36151 0.5 8.13676 0.5 7.86609V1.69084C0.5 1.44084 0.58475 1.23134 0.75425 1.06234C0.923917 0.893178 1.13408 0.808594 1.38475 0.808594ZM2 2.30859V7.30859H16V2.30859H2ZM1.38475 10.1931H16.596C16.8602 10.1931 17.0768 10.2816 17.246 10.4586C17.4153 10.6354 17.5 10.8547 17.5 11.1163V17.2316C17.5 17.5149 17.4153 17.746 17.246 17.9248C17.0768 18.1037 16.8602 18.1931 16.596 18.1931H1.404C1.13983 18.1931 0.923167 18.1037 0.754 17.9248C0.584667 17.746 0.5 17.5149 0.5 17.2316V11.1163C0.5 10.8547 0.581417 10.6354 0.74425 10.4586C0.907083 10.2816 1.12058 10.1931 1.38475 10.1931ZM2 11.6931V16.6931H16V11.6931H2Z"
            fill="#E67700"
          />
        </svg>
      </div>
      <div class="ml-3">
        <p class="ml-1 text-sm text-[#E67700] font-medium"
          >Steps to connect SMTP</p
        >
        <ul class="mt-1 text-sm text-black smtp-steps">
          <li>Make sure your email account has SMTP enabled.</li>
          <li
            >Ensure that two-factor authentication (2FA) is disabled on your
            email account.
          </li>
          <li
            >If 2FA is enabled, please use an app password instead of your
            regular email password.
          </li>
        </ul>
      </div>
    </div>
  </div>

  <form @submit.prevent="onSubmit">
    <div class="grid grid-cols-2 gap-6">
      <div>
        <label class="block mb-2 font-medium text-gray-700" for="smtpHost"
          >SMTP Host
          <i
            v-tooltip="smtpHost.tooltip"
            class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
          ></i>
        </label>
        <input
          id="smtpHost"
          v-model="smtpHost.value"
          placeholder="e.g., smtp.postmarkapp.com"
          :class="{ '!border-red-300': smtpHost.error }"
          :required="smtpHost.isRequired"
          class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 color-border cst-editor"
          type="text"
        />
        <p v-if="smtpHost.error" class="mt-1 text-sm text-red-600">{{
          smtpHost.error
        }}</p>
      </div>
      <div>
        <label class="block mb-2 font-medium text-gray-700" for="smtpPort"
          >SMTP Port
          <i
            v-tooltip="smtpPort.tooltip"
            class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
          ></i>
        </label>
        <input
          id="smtpPort"
          v-model="smtpPort.value"
          placeholder="e.g., 587"
          :class="{ '!border-red-300': smtpPort.error }"
          :required="smtpPort.isRequired"
          class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 color-border cst-editor"
          maxlength="5"
          type="text"
          @input="validateDigits"
        />
        <p v-if="smtpPort.error" class="mt-1 text-sm text-red-600">{{
          smtpPort.error
        }}</p>
      </div>
      <div>
        <label class="block mb-2 font-medium text-gray-700" for="smtpUsername"
          >SMTP Username
          <i
            v-tooltip="smtpUsername.tooltip"
            class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
          ></i>
        </label>
        <input
          id="smtpUsername"
          v-model="smtpUsername.value"
          :class="{ '!border-red-300': smtpUsername.error }"
          :required="smtpUsername.isRequired"
          class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 color-border cst-editor"
          type="text"
        />
        <p v-if="smtpUsername.error" class="mt-1 text-sm text-red-600">{{
          smtpUsername.error
        }}</p>
      </div>
      <div>
        <label class="block mb-2 font-medium text-gray-700" for="smtpPassword"
          >SMTP Password
          <i
            v-tooltip="smtpPassword.tooltip"
            class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
          ></i>
        </label>
        <div class="relative">
          <input
            id="smtpPassword"
            v-model="smtpPassword.value"
            :class="{ '!border-red-300': smtpPassword.error }"
            :required="smtpPassword.isRequired"
            :type="showPassword ? 'text' : 'password'"
            class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 color-border cst-editor"
          />
          <img
            :src="PasswordEyeIconSrc"
            alt="eye"
            class="absolute right-3 top-3 cursor-pointer"
            @click="togglePassword"
          />
        </div>
        <p v-if="smtpPassword.error" class="mt-1 text-sm text-red-600">{{
          smtpPassword.error
        }}</p>
      </div>
      <div>
        <label class="block mb-2 font-medium text-gray-700" for="fromEmail"
          >From Email

          <i
            v-tooltip="fromEmail.tooltip"
            class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
          ></i>
        </label>
        <input
          id="fromEmail"
          v-model="fromEmail.value"
          placeholder="e.g., user@company.com"
          :class="{ '!border-red-300': fromEmail.error }"
          :required="fromEmail.isRequired"
          class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 color-border cst-editor"
          type="email"
        />
        <p v-if="fromEmail.error" class="mt-1 text-sm text-red-600">{{
          fromEmail.error
        }}</p>
      </div>
      <div>
        <label class="block mb-2 font-medium text-gray-700" for="senderName"
          >Sender Name
          <i
            v-tooltip="senderName.tooltip"
            class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
          ></i>
        </label>
        <input
          id="senderName"
          v-model="senderName.value"
          placeholder="e.g., Your Company Name"
          :class="{ '!border-red-300': senderName.error }"
          :required="senderName.isRequired"
          class="w-full border border-gray-300 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 color-border cst-editor"
          type="text"
        />
        <p v-if="senderName.error" class="mt-1 text-sm text-red-600">{{
          senderName.error
        }}</p>
      </div>
    </div>

    <div class="mt-6">
      <label class="block mb-2 font-medium text-gray-700">Encryption</label>
      <div class="flex items-center gap-4">
        <label class="inline-flex items-center">
          <input
            v-model="smtpEncryption.value"
            :class="{ '!border-red-300': smtpEncryption.error }"
            :required="smtpEncryption.isRequired"
            class="text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
            type="radio"
            value="tls"
          />
          <span class="ml-2 cursor-pointer">TLS</span>
        </label>
        <label class="inline-flex items-center">
          <input
            v-model="smtpEncryption.value"
            :required="smtpEncryption.isRequired"
            class="text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
            type="radio"
            value="ssl"
          />
          <span class="ml-2 cursor-pointer">SSL</span>
        </label>
      </div>
      <p v-if="smtpEncryption.error" class="mt-1 text-sm text-red-600">{{
        smtpEncryption.error
      }}</p>
    </div>

    <div class="flex justify-between mt-8">
      <CstButton
        class="!border-gray-cs-500 !text-gray-cs-600"
        variant="outlined"
        @click="emit('prev')"
      >
        Back
      </CstButton>
      <div class="flex items-center gap-2">
        <CstButton
          :disabled="!isFormValid || isLoading"
          class="!border-primary-cs"
          variant="outlined"
          @click="testSMTPConnection"
        >
          Test SMTP Connection
        </CstButton>

        <CstButton
          :disabled="shouldDisableNext"
          :loading="isLoading"
          type="submit"
        >
          Save {{ !isWhiteLabelEnabled ? '& Activate' : '' }}
        </CstButton>
      </div>
    </div>
  </form>
</template>

<style scoped>
.smtp-steps {
  list-style-type: none;
  padding-left: 1.5em;
  margin: 0;
}

.smtp-steps li {
  position: relative;
  margin-bottom: 0.5em;
}

.smtp-steps li::before {
  content: '•';
  position: absolute;
  left: -1em;
  color: black;
  font-size: 1.2em;
  line-height: 1;
}
</style>
